import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestoreIcon from '@mui/icons-material/Restore';
import ScienceIcon from '@mui/icons-material/Science';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';

import {
  modelLabDeploymentApi,
  modelLabVersionApi,
} from '../../services/api';
import type { ModelLabDeployment, ModelLabDeploymentCreate, DeploymentHealth, PredictionResult } from '../../types';
import {
  ConfirmDialog,
  ErrorAlert,
  LoadingSpinner,
  PageHeader,
} from '../common';
import {
  deploymentStatusColor,
  getApiErrorMessage,
  parseJsonInput,
} from './utils';

interface DeploymentFormState {
  name: string;
  description: string;
  modelVersionId: string;
  environment: string;
  tags: string;
  minReplicas: number;
  maxReplicas: number;
  cpuLimit: string;
  memoryLimit: string;
  configJson: string;
}

const initialDeploymentForm: DeploymentFormState = {
  name: '',
  description: '',
  modelVersionId: '',
  environment: 'development',
  tags: '',
  minReplicas: 1,
  maxReplicas: 3,
  cpuLimit: '1',
  memoryLimit: '512Mi',
  configJson: '{}',
};

const ModelLabDeploymentsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deploymentForm, setDeploymentForm] = useState<DeploymentFormState>(
    initialDeploymentForm
  );
  const [deploymentToDelete, setDeploymentToDelete] = useState<ModelLabDeployment | null>(
    null
  );
  // Prediction test panel
  const [predictDeployment, setPredictDeployment] = useState<ModelLabDeployment | null>(null);
  const [predictInput, setPredictInput] = useState('{\n  \n}');
  const [predictionResult, setPredictionResult] = useState<PredictionResult | null>(null);
  // Health check
  const [healthDeployment, setHealthDeployment] = useState<ModelLabDeployment | null>(null);
  const [healthData, setHealthData] = useState<DeploymentHealth | null>(null);
  // Rollback
  const [rollbackDeployment, setRollbackDeployment] = useState<ModelLabDeployment | null>(null);
  const [rollbackVersionId, setRollbackVersionId] = useState('');
  const [search, setSearch] = useState('');

  const deploymentsQuery = useQuery({
    queryKey: ['model-lab-deployments', search],
    queryFn: () => modelLabDeploymentApi.list({ page_size: 100, search: search || undefined }),
  });

  const versionsQuery = useQuery({
    queryKey: ['model-lab-versions-for-deployments'],
    queryFn: () => modelLabVersionApi.list({ page_size: 100 }),
  });

  const createDeploymentMutation = useMutation({
    mutationFn: async (form: DeploymentFormState) => {
      const extraConfig = parseJsonInput(form.configJson, {});
      const payload: ModelLabDeploymentCreate = {
        name: form.name,
        model_version: Number(form.modelVersionId),
        config: {
          description: form.description,
          environment: form.environment,
          tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
          scaling: {
            min_replicas: form.minReplicas,
            max_replicas: form.maxReplicas,
            cpu_limit: form.cpuLimit,
            memory_limit: form.memoryLimit,
          },
          ...extraConfig,
        },
      };
      return modelLabDeploymentApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-deployments'] });
      setCreateOpen(false);
      setDeploymentForm(initialDeploymentForm);
      toast.success('Deployment created.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create deployment.'));
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => modelLabDeploymentApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-deployments'] });
      toast.success('Deployment activated.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to activate deployment.'));
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id: number) => modelLabDeploymentApi.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-deployments'] });
      toast.success('Deployment paused.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to pause deployment.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => modelLabDeploymentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-deployments'] });
      setDeploymentToDelete(null);
      toast.success('Deployment deleted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete deployment.'));
    },
  });

  const predictMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Record<string, any> }) =>
      modelLabDeploymentApi.predict(id, input),
    onSuccess: (data) => {
      setPredictionResult(data);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Prediction failed.'));
    },
  });

  const healthCheckMutation = useMutation({
    mutationFn: (id: number) => modelLabDeploymentApi.healthCheck(id),
    onSuccess: (data) => {
      setHealthData(data);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Health check failed.'));
    },
  });

  const rollbackMutation = useMutation({
    mutationFn: ({ id, versionId }: { id: number; versionId: number }) =>
      modelLabDeploymentApi.rollback(id, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-deployments'] });
      setRollbackDeployment(null);
      setRollbackVersionId('');
      toast.success('Rollback successful.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Rollback failed.'));
    },
  });

  const error = deploymentsQuery.error || versionsQuery.error;
  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  if (deploymentsQuery.isLoading || versionsQuery.isLoading) {
    return <LoadingSpinner message="Loading deployments..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Deployments"
        breadcrumbs={[
          { label: 'Model Lab', to: '/ai/model-lab' },
          { label: 'Deployments' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              disabled={(versionsQuery.data?.results || []).length === 0}
            >
              New Deployment
            </Button>
          </Stack>
        }
      />

      <Stack spacing={2}>
        <Alert severity="info">
          Deployments here manage registry endpoint state and lifecycle only. Wiring
          deployed model versions into AI systems is intentionally deferred.
        </Alert>

        {(versionsQuery.data?.results || []).length === 0 && (
          <Alert severity="warning">
            No model versions are available for deployment yet. Complete a training job first.
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model Version</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Health</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(deploymentsQuery.data?.results || []).map((deployment) => (
                <TableRow key={deployment.id} hover>
                  <TableCell>{deployment.name}</TableCell>
                  <TableCell>{deployment.model_version_name}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {deployment.endpoint_slug}
                      </Typography>
                      <Tooltip title="Copy endpoint URL">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const url = `/api/ai/model-lab/api/deployments/${deployment.id}/predict/`;
                            navigator.clipboard.writeText(url);
                            toast.success('Endpoint URL copied to clipboard');
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={deployment.status}
                      color={deploymentStatusColor(deployment.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {deployment.health?.last_transition || 'No health signal yet'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Test prediction">
                      <span>
                        <IconButton
                          color="secondary"
                          onClick={() => {
                            setPredictDeployment(deployment);
                            setPredictionResult(null);
                            setPredictInput('{\n  \n}');
                          }}
                          disabled={!deployment.is_active}
                        >
                          <ScienceIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Health check">
                      <IconButton
                        color="info"
                        onClick={() => {
                          setHealthDeployment(deployment);
                          setHealthData(null);
                          healthCheckMutation.mutate(deployment.id);
                        }}
                      >
                        <HealthAndSafetyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rollback">
                      <IconButton
                        color="default"
                        onClick={() => {
                          setRollbackDeployment(deployment);
                          setRollbackVersionId('');
                        }}
                      >
                        <RestoreIcon />
                      </IconButton>
                    </Tooltip>
                    {!deployment.is_active && (
                      <IconButton
                        color="primary"
                        onClick={() => activateMutation.mutate(deployment.id)}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    )}
                    {deployment.is_active && (
                      <IconButton
                        color="warning"
                        onClick={() => pauseMutation.mutate(deployment.id)}
                      >
                        <PauseIcon />
                      </IconButton>
                    )}
                    <IconButton
                      color="error"
                      onClick={() => setDeploymentToDelete(deployment)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(deploymentsQuery.data?.results || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No deployments yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Deployment</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* ── Basic Info ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Basic Information
            </Typography>
            <TextField
              label="Deployment name"
              value={deploymentForm.name}
              onChange={(event) =>
                setDeploymentForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              required
              placeholder="e.g. my-model-prod"
            />
            {deploymentForm.name && (
              <Alert severity="info" variant="outlined" icon={<LinkIcon />}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  Endpoint: /api/ml/deployments/{deploymentForm.name.toLowerCase().replace(/\s+/g, '-')}/predict
                </Typography>
              </Alert>
            )}
            <TextField
              label="Description"
              value={deploymentForm.description}
              onChange={(event) =>
                setDeploymentForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={2}
              placeholder="Purpose and notes for this deployment"
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Model version"
                value={deploymentForm.modelVersionId}
                onChange={(event) =>
                  setDeploymentForm((current) => ({
                    ...current,
                    modelVersionId: event.target.value,
                  }))
                }
                fullWidth
                helperText="Select the trained model version to deploy"
              >
                {(versionsQuery.data?.results || []).map((version) => (
                  <MenuItem key={version.id} value={String(version.id)}>
                    <Box>
                      <Typography variant="body2">
                        {version.model_asset_name} / v{version.version_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stage: {version.stage}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Environment"
                value={deploymentForm.environment}
                onChange={(event) =>
                  setDeploymentForm((current) => ({
                    ...current,
                    environment: event.target.value,
                  }))
                }
                fullWidth
                helperText="Target deployment environment"
              >
                <MenuItem value="development">Development</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </TextField>
            </Stack>
            <TextField
              label="Tags"
              value={deploymentForm.tags}
              onChange={(event) =>
                setDeploymentForm((current) => ({
                  ...current,
                  tags: event.target.value,
                }))
              }
              fullWidth
              helperText="Comma separated"
              placeholder="e.g. v2, forex, high-priority"
            />

            <Divider />

            {/* ── Scaling Config ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Scaling & Resources
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                  Min replicas: {deploymentForm.minReplicas}
                </Typography>
                <Slider
                  value={deploymentForm.minReplicas}
                  onChange={(_, val) =>
                    setDeploymentForm((c) => ({
                      ...c,
                      minReplicas: val as number,
                      maxReplicas: Math.max(c.maxReplicas, val as number),
                    }))
                  }
                  min={0}
                  max={10}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 1, label: '1' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                  Max replicas: {deploymentForm.maxReplicas}
                </Typography>
                <Slider
                  value={deploymentForm.maxReplicas}
                  onChange={(_, val) =>
                    setDeploymentForm((c) => ({
                      ...c,
                      maxReplicas: val as number,
                      minReplicas: Math.min(c.minReplicas, val as number),
                    }))
                  }
                  min={1}
                  max={20}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="CPU limit"
                value={deploymentForm.cpuLimit}
                onChange={(event) =>
                  setDeploymentForm((c) => ({ ...c, cpuLimit: event.target.value }))
                }
                fullWidth
                helperText="Max CPU cores allocated per replica"
              >
                <MenuItem value="0.5">0.5 vCPU</MenuItem>
                <MenuItem value="1">1 vCPU</MenuItem>
                <MenuItem value="2">2 vCPUs</MenuItem>
                <MenuItem value="4">4 vCPUs</MenuItem>
              </TextField>
              <TextField
                select
                label="Memory limit"
                value={deploymentForm.memoryLimit}
                onChange={(event) =>
                  setDeploymentForm((c) => ({ ...c, memoryLimit: event.target.value }))
                }
                fullWidth
                helperText="Max memory allocated per replica"
              >
                <MenuItem value="256Mi">256 MB</MenuItem>
                <MenuItem value="512Mi">512 MB</MenuItem>
                <MenuItem value="1Gi">1 GB</MenuItem>
                <MenuItem value="2Gi">2 GB</MenuItem>
                <MenuItem value="4Gi">4 GB</MenuItem>
              </TextField>
            </Stack>

            <Divider />

            {/* ── Advanced Config ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Advanced Configuration (optional)
            </Typography>
            <TextField
              label="Additional config JSON"
              value={deploymentForm.configJson}
              onChange={(event) =>
                setDeploymentForm((current) => ({
                  ...current,
                  configJson: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={4}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              helperText="Extra configuration merged into the deployment config"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createDeploymentMutation.mutate(deploymentForm)}
            disabled={
              !deploymentForm.name ||
              !deploymentForm.modelVersionId ||
              createDeploymentMutation.isPending
            }
          >
            {createDeploymentMutation.isPending ? 'Creating...' : 'Create Deployment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prediction test dialog */}
      <Dialog
        open={Boolean(predictDeployment)}
        onClose={() => setPredictDeployment(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Test Prediction — {predictDeployment?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Provide a JSON input payload matching the model's expected feature schema.
            </Typography>
            <TextField
              label="Input JSON"
              value={predictInput}
              onChange={(e) => setPredictInput(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (predictDeployment) {
                  const parsed = parseJsonInput(predictInput, null);
                  if (!parsed) {
                    toast.error('Invalid JSON input.');
                    return;
                  }
                  predictMutation.mutate({ id: predictDeployment.id, input: parsed });
                }
              }}
              disabled={predictMutation.isPending}
              startIcon={predictMutation.isPending ? <CircularProgress size={16} /> : <ScienceIcon />}
            >
              Run Prediction
            </Button>
            {predictionResult && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Result</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Prediction</Typography>
                      <Typography variant="h6">
                        {JSON.stringify(predictionResult.prediction)}
                      </Typography>
                    </Grid>
                    {predictionResult.probabilities && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Probabilities</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {JSON.stringify(predictionResult.probabilities, null, 2)}
                        </Typography>
                      </Grid>
                    )}
                    {predictionResult.inference_time_ms != null && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Inference Time</Typography>
                        <Typography variant="h6">{predictionResult.inference_time_ms.toFixed(1)} ms</Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPredictDeployment(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Health check dialog */}
      <Dialog
        open={Boolean(healthDeployment)}
        onClose={() => setHealthDeployment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Health Check — {healthDeployment?.name}</DialogTitle>
        <DialogContent>
          {healthCheckMutation.isPending && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {healthData && (
            <Card variant="outlined" sx={{ mt: 1 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="h6">
                      <Chip
                        size="small"
                        label={healthData.status}
                        color={healthData.status === 'ACTIVE' ? 'success' : 'default'}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Model Version</Typography>
                    <Typography variant="h6">{healthData.model_version || '—'}</Typography>
                  </Grid>
                  {healthData.inference_count != null && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Total Inferences</Typography>
                      <Typography variant="h6">{healthData.inference_count}</Typography>
                    </Grid>
                  )}
                  {healthData.last_inference_at && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Last Inference</Typography>
                      <Typography variant="body2">
                        {new Date(healthData.last_inference_at).toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  {healthData.uptime_seconds != null && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Uptime</Typography>
                      <Typography variant="h6">
                        {(healthData.uptime_seconds / 3600).toFixed(1)}h
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHealthDeployment(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Rollback dialog */}
      <Dialog
        open={Boolean(rollbackDeployment)}
        onClose={() => setRollbackDeployment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rollback — {rollbackDeployment?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This will swap the deployed model version. The previous version will remain
              in the registry.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Current version: {rollbackDeployment?.model_version_name}
            </Typography>
            <TextField
              select
              label="Target version"
              value={rollbackVersionId}
              onChange={(e) => setRollbackVersionId(e.target.value)}
              fullWidth
            >
              {(versionsQuery.data?.results || [])
                .filter((v) => v.id !== (rollbackDeployment as any)?.model_version)
                .map((version) => (
                  <MenuItem key={version.id} value={String(version.id)}>
                    {version.model_asset_name} / v{version.version_number} / {version.stage}
                  </MenuItem>
                ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDeployment(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (rollbackDeployment && rollbackVersionId) {
                rollbackMutation.mutate({
                  id: rollbackDeployment.id,
                  versionId: Number(rollbackVersionId),
                });
              }
            }}
            disabled={!rollbackVersionId || rollbackMutation.isPending}
          >
            Rollback
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deploymentToDelete)}
        title="Delete deployment"
        message={`Delete ${deploymentToDelete?.name}? This only removes the deployment record, not the model version itself.`}
        confirmText="Delete"
        confirmColor="error"
        onCancel={() => setDeploymentToDelete(null)}
        onConfirm={() => {
          if (deploymentToDelete) {
            deleteMutation.mutate(deploymentToDelete.id);
          }
        }}
      />
    </Box>
  );
};

export default ModelLabDeploymentsPage;
